// Bautagebuch Models und Interfaces

export interface BautagebuchSubmissionResponseDto {
  id: string;  // Internal UUID (always present)
  submissionId?: string;  // Bautagebuch ID (only after submit)
  caseId?: string;  // Bautagebuch case ID (only after submit)
  organisationId?: number;
  status: BautagebuchStatus;
  destinationId?: string;
  serviceType?: string;
  serviceName?: string;
  data?: string;
  schemaUri?: string;
  submittedAt?: string;  // ISO date (only after submit)
  createdAt?: string;
  updatedAt?: string;
  errorMessage?: string;
  attachmentCount?: number;
  messageType?: string;
  isSimplifiedForm?: boolean;
  reportId?: string;
  reportLifecycleStatus?: ReportLifecycleStatus;
  reportVersionNumber?: number;
  currentReportVersion?: boolean;
  supersedesSubmissionId?: string;
  archivedAt?: string;
  fieldValues?: {[key: string]: any};
}

// Response DTO (alias for clarity)
export interface SubmissionResponseDto extends BautagebuchSubmissionResponseDto { }

// Attachment Response DTO
export interface AttachmentResponseDto {
  id: string;  // Attachment UUID
  fileId?: string;  // File UUID
  submissionId?: string;  // Internal submission UUID
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  hashValue?: string;  // SHA-256 hash
  downloadUrl?: string;
  createdAt?: string;  // ISO date
  anlageartCode?: string;  // XBau anlageart code (e.g., "100", "601", "999")
}

// Pageable request parameters
export interface Pageable {
  page?: number;
  size?: number;
  sort?: string;
}

export enum BautagebuchStatus {
  DRAFT = 'DRAFT',
  INCOMPLETE = 'INCOMPLETE',
  SUBMITTED = 'SUBMITTED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  DELETED = 'DELETED',
  FORWARDED = 'FORWARDED',
  NOTIFIED = 'NOTIFIED',
  // Legacy values (keeping for backward compatibility)
  /*   PENDING = 'PENDING', */
  DELIVERED = 'DELIVERED',
}

// Alias for SubmissionStatus to match backend
export type SubmissionStatus = BautagebuchStatus;

export enum ReportLifecycleStatus {
  DRAFT = 'DRAFT',
  FINALIZED = 'FINALIZED',
  ARCHIVED = 'ARCHIVED'
}

export interface Page<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface DataspacePolicyDto {
  id?: string;
  producerOrgId?: number;
  consumerOrgId: number;
  consumerOrgName?: string;
  projectKey?: string;
  allowedPurpose: string;
  allowedMessageType?: string;
  effect?: string;
  activeFrom?: string | null;
  activeUntil?: string | null;
  usagePolicy?: DataOfferUsagePolicyDto;
  pricing?: DataOfferPricingDto;
  terms?: { [key: string]: any };
}

export interface DataspacePolicyTemplateDto {
  key: string;
  label: string;
  description: string;
  usagePolicy?: DataOfferUsagePolicyDto;
  pricing?: DataOfferPricingDto;
  terms: { [key: string]: any };
}

export interface DataOfferReportFilterDto {
  reportLifecycleStatuses: string[];
  startDate?: string | null;
  endDate?: string | null;
  projectScope?: string;
  searchTerm?: string;
}

export interface DataOfferAccessPolicyDto {
  visibility: 'public' | 'restricted';
  consumerOrgIds: number[];
}

export interface DataOfferUsagePolicyDto {
  mode: 'view-only' | 'compute-to-data' | 'controlled-export';
  permissions?: Array<'VIEW' | 'EXPORT_PDF' | 'EXPORT_XML' | 'COMPUTE_TO_DATA'>;
  exportFormats: string[];
  customTerms: { [key: string]: any };
}

export interface DataOfferPricingDto {
  type: 'FREE' | 'PAID';
  amount?: number | null;
  currency?: string | null;
  billingBasis?: string | null;
}

export interface DataOfferUpsertRequestDto {
  title?: string | null;
  titleCustomized: boolean;
  description?: string | null;
  reportFilter: DataOfferReportFilterDto;
  includedSections: string[];
  accessPolicy: DataOfferAccessPolicyDto;
  usagePolicy: DataOfferUsagePolicyDto;
  pricing: DataOfferPricingDto;
}

export interface DataOfferResponseDto {
  offerId: string;
  producerOrgId: number;
  producerOrgName?: string;
  title: string;
  titleCustomized: boolean;
  description?: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'REVOKED' | 'INVALID' | 'ARCHIVED';
  reportFilter: DataOfferReportFilterDto;
  includedSections: string[];
  accessPolicy: DataOfferAccessPolicyDto;
  usagePolicy: DataOfferUsagePolicyDto;
  pricing: DataOfferPricingDto;
  publishedAt?: string;
  revokedAt?: string;
  archivedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface DataOfferResolvedReportDto {
  reportId: string;
  submissionId: string;
  projectLabel: string;
  reportDate?: string | null;
  reportLifecycleStatus: ReportLifecycleStatus;
  reportVersionNumber?: number | null;
  updatedAt?: string;
  reportPayload?: { [key: string]: any } | null;
}

export interface DataOfferCatalogItemDto {
  offerId: string;
  producerOrgId: number;
  producerOrgName: string;
  title: string;
  description?: string | null;
  policySummary: string;
  status: 'PUBLISHED';
  resolvedReportCount: number;
  includedSections: string[];
  projectLabels: string[];
  firstReportDate?: string | null;
  lastReportDate?: string | null;
  contentAccessible: boolean;
  exportAllowed: boolean;
  paymentRequired: boolean;
  paymentGranted: boolean;
  usagePolicy: DataOfferUsagePolicyDto;
  pricing: DataOfferPricingDto;
  updatedAt?: string;
}

export interface DataOfferCatalogDetailDto extends DataOfferCatalogItemDto {
  requestAccessAllowed: boolean;
  paymentConfirmationAllowed: boolean;
  fakeOrganisationBypassActive: boolean;
  resolvedReports: DataOfferResolvedReportDto[];
}

export interface DataspaceAssetCreateRequestDto {
  assetId?: string;
  sourceSubmissionId: string;
  projectKey: string;
  reportDate?: string | null;
  releasedSectionIds: string[];
  releasedItemPaths: string[];
  policies: DataspacePolicyDto[];
}

export interface DataspaceAssetResponseDto {
  assetId: string;
  sourceSubmissionId: string;
  sourceMessageType: string;
  producerOrgId: number;
  producerOrgName: string;
  projectKey: string;
  reportDate?: string | null;
  status: string;
  version: number;
  lastDispatchedSubmissionId?: string;
  lastDispatchAt?: string;
  transportDestinationId?: string;
  lastDispatchedAssetVersion?: number;
  policies: DataspacePolicyDto[];
  manifest: { [key: string]: any };
  policySummary?: string;
  agreementStatus?: string;
  enforcementStatus?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface NuReleaseDispatchRequestDto {
  assetId?: string;
  sourceSubmissionId: string;
  projectKey: string;
  reportDate?: string | null;
  releasedSectionIds: string[];
  releasedItemPaths: string[];
  consumerOrgIds: number[];
  policyTemplateKey?: string | null;
  policyTerms?: { [key: string]: any } | null;
}

export interface NuReleaseDispatchResponseDto {
  asset: DataspaceAssetResponseDto;
  deliveryMode: string;
  deliveryStatus: string;
  deliveryMessage: string;
}

export interface DataspaceCatalogItemDto {
  assetId: string;
  projectKey: string;
  reportDate?: string | null;
  producerOrgId: number;
  producerOrgName: string;
  sourceMessageType: string;
  version: number;
  releasedSectionIds: string[];
  releasedItemPaths: string[];
  allowedPurpose: string;
  policyTerms?: { [key: string]: any };
  policySummary?: string;
  agreementStatus?: string;
  enforcementStatus?: string;
  requestAccessAllowed?: boolean;
  exportAllowed?: boolean;
  updatedAt?: string;
}

export interface DataspaceAgreementDto {
  id: string;
  accessRequestId?: string;
  assetVersionNumber: number;
  producerOrgId: number;
  consumerOrgId: number;
  consumerOrgName: string;
  status: string;
  activeFrom?: string | null;
  activeUntil?: string | null;
  approvedAt?: string | null;
  revokedAt?: string | null;
  terminatedAt?: string | null;
  statusReason?: string | null;
  policyTerms?: { [key: string]: any };
}

export interface DataspaceAccessRequestDto {
  id: string;
  assetId: string;
  assetVersionNumber: number;
  requesterOrgId: number;
  requesterOrgName: string;
  reason?: string | null;
  status: string;
  decisionReason?: string | null;
  createdAt?: string;
  decidedAt?: string | null;
  agreement?: DataspaceAgreementDto | null;
}

export interface DataspaceAccessRequestCreateDto {
  reason?: string | null;
}

export interface DataspaceAccessRequestDecisionDto {
  decision: string;
  reason?: string | null;
}

export interface DataspaceUsageEventDto {
  id: string;
  agreementId?: string | null;
  assetVersionNumber: number;
  eventType: string;
  outcome: string;
  actorOrgId?: number;
  actorOrgName?: string;
  purpose?: string;
  details?: { [key: string]: any };
  createdAt?: string;
}

export interface DataspaceAuditEntryDto {
  id: string;
  assetVersionNumber?: number;
  entryType: string;
  summary: string;
  actorOrgId?: number;
  actorOrgName?: string;
  details?: { [key: string]: any };
  createdAt?: string;
}

export interface DataspaceAssetVersionDto {
  versionNumber: number;
  superseded: boolean;
  supersededAt?: string | null;
  createdAt?: string;
  changeSummary?: { [key: string]: any };
}

export interface DataspaceAssetDetailDto {
  assetId: string;
  projectKey: string;
  projectLabel: string;
  producerOrgName: string;
  currentVersion: number;
  assetStatus: string;
  agreementStatus: string;
  enforcementStatus: string;
  policySummary: string;
  contentAccessible: boolean;
  exportAllowed: boolean;
  acknowledgeAllowed: boolean;
  requestAccessAllowed: boolean;
  reportPayload?: { [key: string]: any } | null;
  manifest?: { [key: string]: any };
  machineReadablePolicy?: { [key: string]: any };
  agreement?: DataspaceAgreementDto | null;
  versions: DataspaceAssetVersionDto[];
  usageEvents: DataspaceUsageEventDto[];
  auditEntries: DataspaceAuditEntryDto[];
}

export interface DataspaceProjectOverviewDto {
  projectKey: string;
  reportsReceivedToday: number;
  missingReports: number;
  delayedReports: number;
  reportsWithIssues: number;
  reportsWithPolicyRestrictions: number;
}

export interface DataspaceExportResponseDto {
  fileName: string;
  mimeType: string;
  content: string;
  contentEncoding?: string | null;
}

export interface DataspaceRuntimeCleanupResponseDto {
  deletedReportCount: number;
  deletedSubmissionCount: number;
  deletedOfferCount: number;
  deletedFakePaymentGrantCount: number;
  deletedAssetCount: number;
  deletedPolicyCount: number;
  deletedAssetVersionCount: number;
  deletedAccessLogCount: number;
  deletedAccessRequestCount: number;
  deletedAgreementCount: number;
  deletedAgreementDecisionCount: number;
  deletedUsageEventCount: number;
  deletedNotificationCount: number;
  deletedAuditEntryCount: number;
  deletedAttachmentCount: number;
}

export interface DataspaceDemoSeedResponseDto {
  organisationId: number;
  organisationName: string;
  reportCount: number;
  reportIds: string[];
  cleanup?: DataspaceRuntimeCleanupResponseDto | null;
}

export interface DataspacePullRequestDto {
  purpose: string;
}

export interface DataspacePullResponseDto {
  assetId: string;
  purpose: string;
  payload: { [key: string]: any };
}

export interface BautagebuchDataspaceImportRequestDto {
  assetIds: string[];
  purpose: string;
  targetDraftId?: string | null;
  existingFieldValues?: { [key: string]: any };
  assetSelections?: BautagebuchDataspaceAssetSelectionDto[];
}

export interface BautagebuchDataspaceAssetSelectionDto {
  assetId: string;
  selectedSectionIds: string[];
}

export interface BautagebuchImportedWorkerSummaryDto {
  qualifikation?: string | null;
  anzahl?: number | null;
  lohngruppe?: string | null;
}

export interface BautagebuchImportedSourceDto {
  assetId: string;
  producerOrgName?: string | null;
  projectKey?: string | null;
  reportDate?: string | null;
  selectedSectionIds: string[];
  workerSummary: BautagebuchImportedWorkerSummaryDto[];
  workSummary: string[];
  previewFieldValues?: { [key: string]: any } | null;
}

export interface BautagebuchDataspaceImportPreviewDto {
  targetDraftId?: string | null;
  mergedFieldValues: { [key: string]: any };
  mergedXml: string;
  warnings: string[];
  importedAssetIds: string[];
  importedSources: BautagebuchImportedSourceDto[];
}
